import React, { useCallback, useEffect, useState } from "react";
import { View, StyleSheet, FlatList } from "react-native";
import t from "@constants/localization";
import HeaderWithBackAndTitle from "@components/HeaderWithBackAndTitle";
import WalletFilters from "./components/WalletFilter";
import { FilterType, WalletTxnType } from "./utils";
import { useDispatch, useSelector } from "react-redux";
import { useFocusEffect } from "@react-navigation/native";
import { actions as walletActions } from "@reducers/walletReducer";
import TransactionHeaderCard from "./components/TransactionHeaderCard";
import MainTransactionCard from "./components/MainTransactionCard";
import WalletModal from "./components/WalletModal";
import SkeletonLoaderList from "@components/SkeletonLoaderList";
import NoTransactionsView from "./components/NoTransactionsView";
const PATH = "screens/Wallet/Landing";

interface TransactionsViewProps {
  navigation: any;
  route: any;
}

const TransactionsView: React.FC<TransactionsViewProps> = ({
  navigation,
  route,
}) => {
  const dispatch = useDispatch();
  const [modalType, setModalType] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [page, setPage] = useState(1);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { walletTransactions, isLoading } = useSelector(
    (state) => state.walletReducer,
  );

  useEffect(() => {
    if (route?.params?.fromSupport && route?.params?.key) {
      dispatch(walletActions.resetWalletTransactionsData());
      dispatch(walletActions.setTransactionType(route?.params?.key));
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      fetchTransactions();
    }, [page]),
  );

  const fetchTransactions = () => {
    dispatch(walletActions.getWalletTransactions({}));
  };

  const handleLoadMore = () => {
    if (walletTransactions?.hasMore) {
      setPage(page + 1);
    }
  };
  const handleRefresh = () => {
    setIsRefreshing(true);
    dispatch(walletActions.resetWalletTransactionsData());
    setPage(1);
    setIsRefreshing(false);
  };

  const handleApplyFilter = () => {
    setPage(1);
    dispatch(walletActions.resetWalletTransactionsData());
    dispatch(walletActions.getWalletTransactions({}));
  };

  const handleTxnCtaPress = (
    nav_to: string,
    cta_type: string,
    cta_api: string,
    item: object,
  ) => {
    if (nav_to === "MODAL") {
      dispatch(walletActions.setModalApi(cta_api));
      dispatch(walletActions.getModal());
      setModalType(cta_type);
      setShowModal(true);
    } else {
      navigation.navigate(nav_to, { data: item });
    }
  };

  const renderItem = ({ item }) => {
    return (
      <MainTransactionCard
        item={item}
        onPress={handleTxnCtaPress}
        disableCta={false}
      />
    );
  };

  const renderEmptyList = () => {
    return <NoTransactionsView text={"No Transactions"} />;
  };
  const isPenalty = () => {
    const key = walletTransactions?.summary?.key;
    if (
      key == WalletTxnType.FAKE_RTO ||
      key == WalletTxnType.HIGH_DAMAGE ||
      key == WalletTxnType.HIGH_RTO ||
      key == WalletTxnType.RVP_PICKUP_MISSING ||
      key == WalletTxnType.CRATE_PENALTY
    ) {
      return true;
    }
    return false;
  };

  return (
    <>
      <HeaderWithBackAndTitle
        route={route}
        navigation={navigation}
        headertitle={t(`${PATH}/wallet`)}
        subHeaderTitle={undefined}
        headerStyle={undefined}
        rightIcon={undefined}
        RightButton={undefined}
        goingBackHandler={undefined}
        IsRightIconDropDown={undefined}
      />
      {page === 1 && isLoading ? (
        <SkeletonLoaderList length={8} marginHorizontal={16} />
      ) : (
        <View style={styles.container}>
          <TransactionHeaderCard summary={walletTransactions?.summary} />
          <WalletFilters
            filterType={
              isPenalty() ? FilterType.DATEANDTRANSACTION : FilterType.DATEONLY
            }
            onApplyFilter={handleApplyFilter}
          />

          <FlatList
            data={walletTransactions?.transactions}
            keyExtractor={(item) => item.id}
            renderItem={renderItem}
            showsVerticalScrollIndicator={false}
            ListEmptyComponent={renderEmptyList}
            onEndReached={handleLoadMore}
            onEndReachedThreshold={0.5}
            refreshing={isRefreshing}
            onRefresh={handleRefresh}
          />
          <WalletModal
            setShowModal={setShowModal}
            showModal={showModal}
            modalType={modalType}
          />
        </View>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    marginVertical: 16,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 16,
  },
  headerTitle: {
    fontSize: 20,
    marginLeft: 16,
  },
  balanceContainer: {
    backgroundColor: "#fff",
    padding: 16,
    borderRadius: 8,
    marginBottom: 16,
  },
  balanceTextContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  balanceAmount: {
    fontSize: 24,
    fontWeight: "bold",
  },
  balanceLabel: {
    fontSize: 16,
    marginLeft: 8,
    color: "#888",
  },
  feesContainer: {
    marginTop: 8,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  feeText: {
    fontSize: 14,
    color: "#555",
  },
  filterContainer: {
    flexDirection: "row",
    marginBottom: 16,
  },
  filterButton: {
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: "#e9ecef",
    borderRadius: 5,
    marginRight: 8,
  },
  filterText: {
    fontSize: 14,
  },
  dateButton: {
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: "#e9ecef",
    borderRadius: 5,
  },
  dateText: {
    fontSize: 14,
  },
  transactionContainer: {
    backgroundColor: "#fff",
    padding: 16,
    borderRadius: 8,
    marginBottom: 8,
  },
  footerLoader: {
    padding: 10,
    alignItems: "center",
  },
});

export default TransactionsView;
